import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { api, errorMessage, unwrap } from "@/api/client";
import {
  canCancel,
  canDelete,
  canPause,
  canResume,
  canRetry,
  RUNS_KEY,
  type RunSummary,
  useRunActions,
} from "@/api/runs";
import ConfirmDialog from "@/components/confirm-dialog";
import EmptyState from "@/components/empty-state";
import Icon from "@/components/icon";
import { List } from "@/components/list";
import Loader from "@/components/loader";
import PageHeader from "@/components/page-header";
import StatusTag from "@/components/status-tag";
import Text from "@/components/text";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatWhen, LIVE_RUN_STATES } from "@/lib/format";
import { failureSummary, reasonLabel } from "@/lib/reasons";

export function RunsPage() {
  const runs = useQuery({
    queryKey: RUNS_KEY,
    queryFn: () => unwrap(api.GET("/api/runs")),
    refetchInterval: (query) => (query.state.data?.some((r) => LIVE_RUN_STATES.has(r.state)) ? 3000 : 15000),
  });
  const live = runs.data?.filter((r) => LIVE_RUN_STATES.has(r.state)).length ?? 0;

  return (
    <>
      <PageHeader
        title="Runs"
        description={runs.data?.length ? `${runs.data.length} runs${live ? `, ${live} in progress` : ""}` : undefined}
        actions={
          <Button asChild>
            <Link to="/runs/new">
              <Icon name="add" size={18} />
              New run
            </Link>
          </Button>
        }
      />

      <List>
        {runs.isPending && (
          <div className="grid place-items-center py-16 text-muted-foreground">
            <Loader />
          </div>
        )}
        {runs.isError && <Text.Danger className="px-(--list-inset) py-4">{errorMessage(runs.error)}</Text.Danger>}
        {runs.data?.length === 0 && (
          <EmptyState
            title="No runs yet"
            description="Paste or upload a list of numbers and their code pages to start registering."
            action={
              <Button asChild>
                <Link to="/runs/new">New run</Link>
              </Button>
            }
          />
        )}
        {!!runs.data?.length && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Run</TableHead>
                <TableHead>Progress</TableHead>
                <TableHead className="text-end">Registered</TableHead>
                <TableHead className="text-end">Failed</TableHead>
                <TableHead className="text-end">Accepted</TableHead>
                <TableHead className="text-end">Rejected</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.data.map((run) => (
                <RunRow key={run.id} run={run} />
              ))}
            </TableBody>
          </Table>
        )}
      </List>
    </>
  );
}

function RunRow({ run }: { run: RunSummary }) {
  const navigate = useNavigate();
  const c = run.counts;
  const settled = c.ok + c.failed;
  const pct = c.total ? Math.round((settled / c.total) * 100) : 0;

  return (
    <TableRow className="cursor-pointer" onClick={() => navigate(`/runs/${run.id}`)}>
      <TableCell>
        <div className="flex flex-col gap-0.5">
          <Link
            to={`/runs/${run.id}`}
            className="font-medium text-foreground hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {run.name}
          </Link>
          <Text.Hint size="caption">
            {formatWhen(run.created_at)} · {run.submit_type}
            {run.source === "provider" ? " · bought numbers" : ""}
            {run.round > 1 ? ` · round ${run.round}` : ""}
            {run.parent_name ? ` · retry of ${run.parent_name}` : ""}
          </Text.Hint>
        </div>
      </TableCell>
      <TableCell className="w-56">
        <div className="flex items-center gap-3">
          <Progress value={pct} className="h-1.5 w-28" />
          <Text.Hint size="caption">
            {settled} / {c.total}
          </Text.Hint>
        </div>
      </TableCell>
      <TableCell className="text-end">{c.ok}</TableCell>
      <TableCell className="text-end">{c.failed ? <Text.Danger>{c.failed}</Text.Danger> : 0}</TableCell>
      <TableCell className="text-end">
        {run.verify.accepted}
        {run.verify.pending > 0 && <Text.Hint size="caption"> +{run.verify.pending} waiting</Text.Hint>}
      </TableCell>
      <TableCell className="text-end">{run.verify.rejected}</TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <StatusTag state={run.state} />
          {run.halt_reason && (
            <Text.Danger size="caption">
              {reasonLabel(run.halt_reason)}
              {run.halt_reason === "too_many_failures" && run.failure_reasons.length > 0
                ? `: ${failureSummary(run.failure_reasons, 1)}`
                : ""}
            </Text.Danger>
          )}
        </div>
      </TableCell>
      <TableCell onClick={(e) => e.stopPropagation()}>
        <RunMenu run={run} />
      </TableCell>
    </TableRow>
  );
}

function RunMenu({ run }: { run: RunSummary }) {
  const navigate = useNavigate();
  const { cancel, pause, resume, retry, remove } = useRunActions();
  const [confirmDelete, setConfirmDelete] = useState(false);
  return (
    <>
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={`Delete "${run.name}"?`}
        description={`Its ${run.counts.total} rows go with it. Accounts already submitted are kept.`}
        confirmLabel="Delete run"
        destructive
        onConfirm={() => remove.mutate(run.id)}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="size-8" aria-label={`Actions for ${run.name}`}>
            <Icon name="more" size={20} className="text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <DropdownMenuItem onSelect={() => navigate(`/runs/${run.id}`)}>
            <Icon name="document" size={18} />
            Open
          </DropdownMenuItem>
          {canResume(run) && (
            <DropdownMenuItem onSelect={() => resume.mutate(run.id)}>
              <Icon name="play" size={18} />
              Continue
            </DropdownMenuItem>
          )}
          {canPause(run) && (
            <DropdownMenuItem onSelect={() => pause.mutate(run.id)}>
              <Icon name="pause" size={18} />
              Pause
            </DropdownMenuItem>
          )}
          {canRetry(run) && (
            <DropdownMenuItem onSelect={() => retry.mutate(run.id)}>
              <Icon name="retry" size={18} />
              Retry {run.retryable_failed} failed
            </DropdownMenuItem>
          )}
          {canCancel(run) && (
            <DropdownMenuItem onSelect={() => cancel.mutate(run.id)}>
              <Icon name="stop" size={18} />
              Cancel run
            </DropdownMenuItem>
          )}
          {canDelete(run) && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" onSelect={() => setConfirmDelete(true)}>
                <Icon name="trash" size={18} />
                Delete
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
